import { useMemo } from "react";

import useCurrentTime from "./useCurrentTime";

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const getNextCycleDate = (
  anchorDate: Date,
  durationDays: number,
  nowMs: number
): Date => {
  const periodMs = durationDays * DAY_MS;
  const anchorMs = anchorDate.getTime();

  if (nowMs < anchorMs) {
    return new Date(anchorMs);
  }

  const elapsedCycles = Math.floor((nowMs - anchorMs) / periodMs);
  return new Date(anchorMs + (elapsedCycles + 1) * periodMs);
};

export const getTimeRemaining = (endDate: Date, nowMs: number): TimeRemaining => {
  const remainingSeconds = Math.max(0, Math.ceil((endDate.getTime() - nowMs) / 1000));

  return {
    days: Math.floor(remainingSeconds / 86400),
    hours: Math.floor((remainingSeconds % 86400) / 3600),
    minutes: Math.floor((remainingSeconds % 3600) / 60),
    seconds: remainingSeconds % 60,
  };
};

const useCycleCountdown = (anchorDate: Date | null, durationDays: number) => {
  const currentTime = useCurrentTime();

  return useMemo(() => {
    if (!anchorDate) {
      return { nextDate: null, timeRemaining: null };
    }
    const nowMs = currentTime * 1000;
    const nextDate = getNextCycleDate(anchorDate, durationDays, nowMs);
    return {
      nextDate,
      timeRemaining: getTimeRemaining(nextDate, nowMs),
    };
  }, [anchorDate, currentTime, durationDays]);
};

export default useCycleCountdown;
